import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../stores/auth.store';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import ParticleCanvas from '../components/ui/ParticleCanvas';

type Step = 'email' | 'code';

export default function Login() {
  const navigate = useNavigate();
  const { user, login, verify, isAuthenticating, error } = useAuthStore();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);

  useEffect(() => {
    if (user) navigate(user.onboardingCompleted ? '/dashboard' : '/onboarding', { replace: true });
  }, [user, navigate]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    if (!email.trim()) return;
    if (!tosAccepted) { setLocalError('You must accept the Terms of Service to continue.'); return; }
    try {
      await login(email.trim().toLowerCase());
      setStep('code');
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to send code');
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    if (!code.trim()) return;
    try {
      await verify(email, code.trim());
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Invalid code');
    }
  };

  const err = localError || error;

  return (
    <div
      className="min-h-screen bg-bot-base flex items-center justify-center px-4 relative overflow-hidden"
      style={{
        backgroundImage: `
          linear-gradient(rgba(34,34,74,0.4) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34,34,74,0.4) 1px, transparent 1px),
          radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,229,255,0.05) 0%, transparent 60%)
        `,
        backgroundSize: '32px 32px, 32px 32px, 100% 100%',
      }}
    >
      {/* Subtle particle neural network in background */}
      <ParticleCanvas
        count={22}
        color="0,229,255"
        secondaryColor="136,68,255"
        connectRadius={140}
        speed={0.3}
        className="absolute inset-0 opacity-20 pointer-events-none"
      />
      <div className="w-full max-w-sm relative z-10">

        {/* ── Logo ──────────────────────────────────────────────── */}
        <div className="flex flex-col items-center mb-8">
          <p className="font-bold text-2xl text-brand-400 uppercase mb-1">
            CoordinAgents
          </p>
          <p className="text-xs font-medium text-ink-mid">Authentication Portal</p>
        </div>

        {/* ── Auth panel ────────────────────────────────────────── */}
        <div className="panel relative rounded-lg">
          {/* Panel top status bar */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-bot-mid bg-bot-raised rounded-t-lg">
            <div className="led led-green" style={{ width: 5, height: 5 }} />
            <span className="text-xs font-medium text-ink-mid flex-1">
              {step === 'email' ? 'Enter your email to sign in' : 'Enter verification code'}
            </span>
            <span className="text-xs text-ink-dim">
              Step {step === 'email' ? '1' : '2'} of 2
            </span>
          </div>

          <div className="p-6">
            {step === 'email' ? (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <p className="text-base text-ink-full font-bold mb-0.5">Sign In</p>
                  <p className="text-xs text-ink-mid">
                    Enter email — new users are registered automatically
                  </p>
                </div>

                <Input
                  type="email"
                  label="EMAIL ADDRESS"
                  placeholder="operator@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  icon={<Mail size={14} />}
                  autoFocus
                  autoComplete="email"
                />

                {err && (
                  <div className="border border-cyber-red/40 bg-cyber-red-dim px-3 py-2 rounded-md">
                    <p className="text-xs text-cyber-red">{err}</p>
                  </div>
                )}

                {/* ToS consent */}
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tosAccepted}
                    onChange={e => setTosAccepted(e.target.checked)}
                    className="mt-0.5 accent-brand-400"
                  />
                  <span className="text-xs text-ink-dim leading-relaxed">
                    I agree to the{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline">Privacy Policy</a>.
                    Agents may send outreach on my behalf; I confirm I have proper consent for contacts.
                  </span>
                </label>

                <Button type="submit" className="w-full" isLoading={isAuthenticating} disabled={!tosAccepted}>
                  Send Code <ArrowRight size={12} />
                </Button>

                <p className="text-xs text-ink-dim text-center">
                  No account? Enter email to auto-register
                </p>
              </form>
            ) : (
              <form onSubmit={handleCodeSubmit} className="space-y-4">
                <div>
                  <p className="text-base text-ink-full font-bold mb-0.5">
                    Verify Code
                  </p>
                  <p className="text-xs text-ink-mid">
                    6-digit code sent to{' '}
                    <span className="text-brand-400">{email}</span>
                  </p>
                </div>

                {/* Big code input */}
                <div>
                  <label className="text-xs font-medium text-ink-mid block mb-1.5">VERIFICATION CODE</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="_ _ _ _ _ _"
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full bg-bot-base border border-bot-mid px-4 py-4 text-3xl text-center tracking-[0.5em] text-brand-400 placeholder-bot-high font-mono font-bold outline-none transition-all rounded-md"
                    onFocus={e => (e.target.style.borderColor = 'var(--cyan)')}
                    onBlur={e => (e.target.style.borderColor = '')}
                    autoFocus
                  />
                </div>

                {err && (
                  <div className="border border-cyber-red/40 bg-cyber-red-dim px-3 py-2 rounded-md">
                    <p className="text-xs text-cyber-red">{err}</p>
                  </div>
                )}

                <Button type="submit" className="w-full" isLoading={isAuthenticating}>
                  Authenticate <ArrowRight size={12} />
                </Button>

                <button
                  type="button"
                  onClick={() => { setStep('email'); setCode(''); setLocalError(''); }}
                  className="w-full text-xs text-ink-dim hover:text-brand-400 transition-colors text-center"
                >
                  ← Use different email
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom system info */}
        <div className="flex items-center justify-center gap-3 mt-6">
          <div className="led led-green" style={{ width: 5, height: 5 }} />
          <span className="text-xs font-medium text-ink-mid">Secure Connection</span>
          <div className="led led-cyan" style={{ width: 5, height: 5 }} />
        </div>
      </div>
    </div>
  );
}
